import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useState } from 'react';
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Link, Route, Routes } from 'react-router-dom';

import { getUsers } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { User } from '../../types';
import AnalyticsHeader from '../../widgets/Header';
import CreateUserModal from './CreateUserModal';
import UserSuccessModal from './UserSuccessModal';
import UserDetail from './ViewAndEditDetail/ViewAndEditDetail';

export const USERS_QUERY_KEY = ['users'];

const RegisterUserButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> Register User </Button>;
};

const columnHelper = createColumnHelper<User>();

const columns = [
  columnHelper.accessor('fullName', {
    header: 'Name',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  // columnHelper.accessor('role', {
  //   header: 'Role',
  //   cell: info =>info.getValue(),
  //   footer: info => info.column.id
  // }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('nickName', {
    header: 'Nickname',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('phoneNumber', {
    header: 'Phone',
    cell: info => info.getValue(),
    footer: info => info.column.id
  })
];

interface UserTableProps {
  query: Record<string, unknown>;
}

const UsersTable = ({ query }: UserTableProps) => {
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [password, setPassword] = useState('');
  const compositeQueryKey = [...USERS_QUERY_KEY, query];
  const { data, isLoading, refetch } = useQuery({ queryKey: compositeQueryKey, queryFn: () => getUsers(query) });
  const users = data?.results;

  const onCreateButtonClicked = () => {
    setShowCreateUserModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateUserModal(false);
  };

  const handleUserCreated = (generatedPassword: string) => {
    setPassword(generatedPassword);
    setShowSuccessModal(true);
    refetch();
  };

  return (
    <>
      {showCreateUserModal && (
        <CreateUserModal
          show={showCreateUserModal}
          handleClose={onCreateModalClosed}
          onUserCreated={handleUserCreated}
        />
      )}
      {showSuccessModal && (
        <UserSuccessModal show={showSuccessModal} handleClose={() => setShowSuccessModal(false)} password={password} />
      )}
      <AnalyticsHeader
        title="Users"
        headerButton={<RegisterUserButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : users && users.length > 0 ? (
              <TableList users={users!} columns={columns} />
            ) : (
              <EmptyTableState message="No users yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'Active',
    path: 'active',
    tabLink: '/users/active',
    element: <UsersTable query={{ isActive: true }} />
  },
  {
    title: 'Inactive',
    path: 'inactive',
    tabLink: '/users/inactive',
    element: <UsersTable query={{ isActive: false }} />
  }
];

const userRoutes = [
  {
    path: '/:status/:id',
    element: <UserDetail />
  }
];

const Users = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {userRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Users;

interface TableListProps {
  users: User[];
  columns: AccessorKeyColumnDef<User, string>[];
}

const TableList = ({ users, columns }: TableListProps) => {
  const tableInstance = useReactTable({ data: users!, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <td key={cell.id}>
                      <Link to={`../user/${row.original._id}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
